import type z from "zod";
import { lifeError } from "@/shared/error";
import { hmr } from "@/shared/hmr";
import * as op from "@/shared/operation";
import { LifeClient } from "./client";
import { type LifeClientOptions, lifeClientOptionsSchema } from "./options";

// Create an HMR-resistant cache map
type ClientCache = Map<string, op.ToPublic<LifeClient>>;
declare global {
  var __LIFE_CLIENT_CACHE__: ClientCache | undefined;
}
const getClientCache = (): ClientCache => {
  if (!globalThis.__LIFE_CLIENT_CACHE__) globalThis.__LIFE_CLIENT_CACHE__ = new Map();
  return globalThis.__LIFE_CLIENT_CACHE__;
};

// Helper to generate a cache key
const getCacheKey = (options: LifeClientOptions): string =>
  `${options.serverUrl}::${options.serverToken ?? ""}`;

/**
 * Creates a new Life.js client instance, which is the main entry point
 * to interact with your Life.js agents.
 *
 * @param options - Client options.
 * @returns LifeClient instance.
 */
export const createLifeClient = (
  options: z.input<typeof lifeClientOptionsSchema> = {},
): op.ToPublic<LifeClient> => {
  // Validate options
  const { error: errOptions, data: parsedOptions } = lifeClientOptionsSchema.safeParse(options);
  if (errOptions) {
    throw lifeError({
      code: "Validation",
      message: "Invalid options provided to LifeClient.",
      cause: errOptions,
    });
  }

  // On the server, simply return the options
  if (typeof window === "undefined") return { options: parsedOptions } as op.ToPublic<LifeClient>;

  // Get HMR-resistant cache and generate cache key
  const cache = getClientCache();
  const key = getCacheKey(parsedOptions);

  // Return any cached client for these options
  const client = cache.get(key);
  if (client) return client;

  // Create a new client and cache it
  const newClient = op.toPublic(new LifeClient(parsedOptions));
  cache.set(key, newClient);

  return newClient;
};

// If nobody higher up accepts hot-reloading, bundlers may trigger a full page reload,
// which would wipe globalThis. Accepting here prevents this module from causing that.
hmr.accept?.();
